import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, SectionTitle, Field } from "@/components/ui-hud";
import BackToCockpit from "@/components/BackToCockpit";
import AirportInput from "@/components/AirportInput";
import { useAuth } from "@/context/AuthContext";
import { FileText, ShieldCheck, Plane, Clock, Printer, Save, Send, AlertTriangle } from "lucide-react";

/**
 * Pilot History Form — insurance underwriter-ready.
 *
 * Modeled on standard aviation insurance PHF templates (Wholesure / USAIG / Global
 * Aerospace) — fillable and persistent. Auto-composes core identity from the PJP
 * profile so pilots only fill the delta.
 *
 * NOTE: We don't have the exact Wholesure PDF field-list yet; fields below cover the
 * ~95% overlap across major underwriters (identity, medical, ratings, time by class,
 * time in type, recent experience, training history, claims/accidents/enforcement,
 * employment history). Once you share the PDF I can map its exact field IDs.
 */

/**
 * Common private-jet type-rating list used by PHF aircraft dropdowns.
 * Covers the biggest business-jet families — pilots can also type-in a value.
 * Sorted alphabetically; "Other" always pinned to the bottom.
 */
const AIRCRAFT_TYPES = [
  "Airbus ACJ319 / ACJ320neo",
  "Beechjet 400A / Hawker 400XP",
  "Boeing BBJ / BBJ 2 / BBJ 3 / BBJ MAX",
  "Bombardier Challenger 300 / 350 / 3500",
  "Bombardier Challenger 604",
  "Bombardier Challenger 605",
  "Bombardier Challenger 650",
  "Bombardier Global 5000 / 5500",
  "Bombardier Global 6000 / 6500",
  "Bombardier Global 7500 / 8000",
  "Bombardier Learjet 35 / 36",
  "Bombardier Learjet 45",
  "Bombardier Learjet 60 / 60XR",
  "Bombardier Learjet 75",
  "Cessna Citation Ascend",
  "Cessna Citation CJ1 / M2",
  "Cessna Citation CJ2 / CJ2+",
  "Cessna Citation CJ3 / CJ3+",
  "Cessna Citation CJ4",
  "Cessna Citation Latitude",
  "Cessna Citation Longitude",
  "Cessna Citation Sovereign / Sovereign+",
  "Cessna Citation X / X+ / Citation Ten",
  "Cessna Citation XLS / XLS+ / XLS Gen2",
  "Cirrus Vision Jet (SF50)",
  "Dassault Falcon 10X",
  "Dassault Falcon 2000 / 2000LX / 2000LXS",
  "Dassault Falcon 6X",
  "Dassault Falcon 7X",
  "Dassault Falcon 8X",
  "Dassault Falcon 900",
  "Embraer Legacy 450 / 500",
  "Embraer Legacy 600 / 650",
  "Embraer Lineage 1000",
  "Embraer Phenom 100 / 100EV / 100EX",
  "Embraer Phenom 300 / 300E / 300MED",
  "Embraer Praetor 500",
  "Embraer Praetor 600",
  "Gulfstream G280",
  "Gulfstream G450 / GIV-X",
  "Gulfstream G500",
  "Gulfstream G550 / GV-SP",
  "Gulfstream G600",
  "Gulfstream G650 / G650ER",
  "Gulfstream G700",
  "Gulfstream G800",
  "Hawker 4000",
  "Hawker 800 / 800XP / 850XP / 900XP",
  "HondaJet HA-420 / Elite / 2600",
  "King Air 200 / B200 / 250",
  "King Air 300 / 350 / 350i / 360",
  "Pilatus PC-12 / PC-12 NG / PC-12 NGX",
  "Pilatus PC-24",
  "TBM 700 / 850 / 910 / 930 / 940 / 960",
  "Other (type in below)",
];

const NUM = "^[0-9]*$";

export default function PHF() {
  const { user } = useAuth();
  const [phf, setPhf] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get("/phf/mine");
      setPhf(r.data);
      if (r.data.form && Object.keys(r.data.form).length > 0) {
        setForm({ ...defaultForm(), ...r.data.form });
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/phf/mine", { form });
      toast.success("PHF saved — insurance-ready snapshot updated");
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return <div className="max-w-4xl mx-auto px-6 py-16 text-white/60">Sign in to complete your PHF.</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (section, k, v) => setForm((f) => ({ ...f, [section]: { ...(f[section] || {}), [k]: v } }));

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="phf-page">
      <BackToCockpit />
      <SectionTitle
        overline="// Pilot History Form (PHF)"
        title="Insurance-ready pilot record."
        subtitle="Composed live from your PJP profile — fill in the extras insurers need. Auto-syncs to Acrisure once enrolled."
      />

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Badge tone="avail" pulse>Live Snapshot</Badge>
        <span className="chip chip-neutral">{phf?.saved_at ? `Saved ${new Date(phf.saved_at).toLocaleDateString()}` : "Not yet saved"}</span>
        <button
          onClick={async () => {
            try {
              const tok = localStorage.getItem("pjp_token");
              const params = new URLSearchParams();
              if (form.subject?.aircraft_type) params.set("target_type", form.subject.aircraft_type);
              // Per-user cache-buster — every fetch gets a unique URL so no
              // browser / CDN / service-worker ever returns another pilot's
              // PDF. Combined with backend Cache-Control: no-store below.
              params.set("_", `${user?.id || ""}-${Date.now()}`);
              const qs = params.toString();
              const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/phf/mine.pdf${qs ? `?${qs}` : ""}`;
              const res = await fetch(url, {
                headers: tok ? { Authorization: `Bearer ${tok}` } : {},
                cache: "no-store",
              });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const blob = await res.blob();
              const dl = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = dl;
              a.download = `PJP-PHF-${user.last_name || ""}-${user.first_name || ""}.pdf`.replace(/\s+/g, "_");
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(dl);
              toast.success("PHF PDF downloaded");
            } catch (e) {
              toast.error("PDF download failed");
            }
          }}
          data-testid="phf-download-pdf"
          className="btn btn-sm ml-auto"
        >
          <Printer className="h-3.5 w-3.5" /> Download PDF
        </button>
        <button onClick={save} disabled={saving} data-testid="phf-save" className="btn btn-sm" style={{ background: "#4B9CFF", color: "#0A1220" }}>
          {saving ? "Saving…" : <><Save className="h-3.5 w-3.5" /> Save PHF</>}
        </button>
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : (
        <div className="space-y-5">
          {/* Auto-composed identity from PJP profile */}
          <Card className="hud-corners relative">
            <span className="hud-c-bl" /><span className="hud-c-br" />
            <div className="flex items-center gap-3 mb-4"><FileText className="h-5 w-5 text-sky" /><div className="font-display text-xl text-white">Auto-composed from your PJP profile</div></div>
            <div className="grid md:grid-cols-2 gap-6">
              <Row label="Legal name"          value={phf.pilot.legal_name} />
              <Row label="DOB"                 value={phf.pilot.dob || "— (set in profile)"} />
              <Row label="Email"               value={phf.pilot.email} />
              <Row label="Phone"               value={phf.pilot.phone || "—"} />
              <Row label="Home base"           value={phf.pilot.home_base || "—"} />
              <Row label="FAA Airman ID"       value={phf.pilot.faa_airman_id || "—"} mono />
              <Row label="Pilot certificate #" value={phf.pilot.pilot_certificate_number || "—"} mono />
              <Row label="Location"            value={phf.pilot.location || "—"} />
            </div>
          </Card>

          {/* Subject aircraft — what this PHF is being underwritten for */}
          <Section title="Model to be flown / requesting approval for" icon={Plane}>
            <div className="mb-3 text-[12px] text-white/60 leading-relaxed" data-testid="phf-subject-help">
              This is the aircraft the underwriter is being asked to approve you on. It drives the
              verification banner on the PDF (Verified / Waiver Recommended / Insufficient) and — if
              you tick the box below — a formal request to add you as <span className="text-cyan">Additionally Insured</span> to
              the tail's policy.
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <FormField
                label="Aircraft — model to be flown *"
                value={form.subject?.aircraft_type}
                onChange={(v) => setNested("subject", "aircraft_type", v)}
                options={["", ...AIRCRAFT_TYPES]}
                testId="phf-subject-aircraft"
              />
              <FormField
                label="Tail number"
                value={form.subject?.tail_number}
                onChange={(v) => setNested("subject", "tail_number", v.toUpperCase())}
                testId="phf-subject-tail"
                placeholder="N123AB"
              />
              <FormField
                label="Aircraft serial #"
                value={form.subject?.serial_number}
                onChange={(v) => setNested("subject", "serial_number", v)}
                testId="phf-subject-serial"
                placeholder="e.g. 4567"
              />
            </div>

            <label className="mt-4 flex items-start gap-3 cursor-pointer group" data-testid="phf-additional-insured-wrap">
              <input
                type="checkbox"
                data-testid="phf-additional-insured"
                checked={!!form.subject?.request_additional_insured}
                onChange={(e) => setNested("subject", "request_additional_insured", e.target.checked)}
                className="mt-1 h-4 w-4 accent-cyan cursor-pointer"
              />
              <div>
                <div className="text-sm text-white group-hover:text-cyan transition-colors">
                  Request to be added as <span className="text-cyan font-semibold">Additionally Insured</span> on the tail # / serial # policy above.
                </div>
                <div className="text-[11px] text-white/50 mt-0.5">
                  PJP will route this to Acrisure Aerospace for the fast-track underwriter workflow
                  once the owner-operator has opted in. Approval is instant for verified members with
                  ≥500 hrs in-type or a green PJP verdict.
                </div>
              </div>
            </label>
          </Section>

          <Section title="Medical Certificate" icon={ShieldCheck}>
            <div className="grid md:grid-cols-3 gap-4">
              <FormField label="Class" value={form.medical?.class} onChange={(v) => setNested("medical", "class", v)} options={["First", "Second", "Third", "BasicMed"]} testId="phf-med-class" />
              <FormField label="Issue date" value={form.medical?.issued_at} onChange={(v) => setNested("medical", "issued_at", v)} type="date" testId="phf-med-issued" />
              <FormField label="Expires" value={form.medical?.expires_at} onChange={(v) => setNested("medical", "expires_at", v)} type="date" testId="phf-med-exp" />
              <FormField label="AME name" value={form.medical?.ame_name} onChange={(v) => setNested("medical", "ame_name", v)} />
              <FormField label="Restrictions / waivers" value={form.medical?.restrictions} onChange={(v) => setNested("medical", "restrictions", v)} className="md:col-span-2" />
            </div>
          </Section>

          {/* Flight time */}
          <Section title="Total Flight Time" icon={Clock}>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                ["total_time", "Total time"],
                ["pic", "PIC"],
                ["sic", "SIC"],
                ["night", "Night"],
                ["instrument_actual", "Instrument (actual)"],
                ["instrument_sim", "Instrument (sim/hood)"],
                ["multi_engine", "Multi-engine"],
                ["turbine", "Turbine"],
                ["turbojet", "Turbojet"],
                ["cross_country", "Cross-country"],
                ["last_12_months", "Last 12 months"],
                ["last_90_days", "Last 90 days"],
              ].map(([k, label]) => (
                <FormField key={k} label={label} value={form.time?.[k]} onChange={(v) => setNested("time", k, v)} inputMode="numeric" pattern={NUM} testId={`phf-time-${k}`} />
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <FormField label="Last flight date" value={form.time?.last_flight_date} onChange={(v) => setNested("time", "last_flight_date", v)} type="date" />
              <FormField label="Anticipated total hours next 12mo" value={form.time?.anticipated_next_year} onChange={(v) => setNested("time", "anticipated_next_year", v)} inputMode="numeric" pattern={NUM} />
            </div>
          </Section>

          {/* Time in type — repeatable rows */}
          <Section title="Time in Type (last 12 months + total)" icon={Plane}>
            <RepeatableTypeTable
              rows={form.time_in_type || []}
              onChange={(rows) => set("time_in_type", rows)}
            />
          </Section>

          {/* Training */}
          <Section title="Training History">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Last recurrent training provider" value={form.training?.recurrent_provider} onChange={(v) => setNested("training", "recurrent_provider", v)} options={["", "FlightSafety International", "CAE", "SimCom", "Textron/TAI", "Bombardier Safety Standdown", "Gulfstream", "In-house / Company", "Other"]} />
              <FormField label="Last recurrent date" value={form.training?.recurrent_date} onChange={(v) => setNested("training", "recurrent_date", v)} type="date" />
              <FormField label="Recurrent aircraft/type" value={form.training?.recurrent_aircraft} onChange={(v) => setNested("training", "recurrent_aircraft", v)} options={["", ...AIRCRAFT_TYPES]} testId="phf-training-aircraft" />
              <FormField label="Type: Initial or Recurrent?" value={form.training?.recurrent_kind} onChange={(v) => setNested("training", "recurrent_kind", v)} options={["", "Initial", "Recurrent", "Differences", "Upgrade"]} />
              <FormField label="Simulator or Aircraft?" value={form.training?.medium} onChange={(v) => setNested("training", "medium", v)} options={["", "Full-motion sim", "FTD", "Aircraft"]} />
              <FormField label="LOA/Waiver on file?" value={form.training?.loa} onChange={(v) => setNested("training", "loa", v)} options={["", "None", "RVSM", "PBN", "SVT", "EFVS", "Other"]} />
            </div>
          </Section>

          {/* Claims / Incidents / Enforcement */}
          <Section title="Claims, Accidents, Incidents & FAA Actions" icon={AlertTriangle}>
            <div className="grid md:grid-cols-3 gap-4">
              <FormField label="Any prior claims?" value={form.claims?.has_claims} onChange={(v) => setNested("claims", "has_claims", v)} options={["", "No", "Yes"]} />
              <FormField label="Any accidents?" value={form.claims?.has_accidents} onChange={(v) => setNested("claims", "has_accidents", v)} options={["", "No", "Yes"]} />
              <FormField label="Any incidents?" value={form.claims?.has_incidents} onChange={(v) => setNested("claims", "has_incidents", v)} options={["", "No", "Yes"]} />
              <FormField label="FAA violations / enforcement?" value={form.claims?.has_enforcement} onChange={(v) => setNested("claims", "has_enforcement", v)} options={["", "No", "Yes"]} />
              <FormField label="Medical denial or SODA?" value={form.claims?.medical_denial} onChange={(v) => setNested("claims", "medical_denial", v)} options={["", "No", "Yes"]} />
              <FormField label="DUI/DWI on record?" value={form.claims?.dui} onChange={(v) => setNested("claims", "dui", v)} options={["", "No", "Yes"]} />
            </div>
            <FormField label="If YES to any above — provide dates & details" value={form.claims?.details} onChange={(v) => setNested("claims", "details", v)} textarea rows={4} className="mt-4" testId="phf-claims-details" />
          </Section>

          {/* Employment */}
          <Section title="Employment / Aircraft Operated">
            <FormField label="Current employer / operator" value={form.employment?.current_employer} onChange={(v) => setNested("employment", "current_employer", v)} />
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <FormField label="Current position / seat" value={form.employment?.current_seat} onChange={(v) => setNested("employment", "current_seat", v)} options={["", "PIC", "SIC", "Chief Pilot", "Director of Ops", "Check Airman"]} />
              <Field label="Base ICAO">
                <AirportInput
                  value={form.employment?.base_icao}
                  onChange={(v) => setNested("employment", "base_icao", v)}
                  testId="phf-employment-base-icao"
                  placeholder="Type city or ICAO (KTEB, Miami…)"
                />
              </Field>
              <FormField label="Cert type of employer" value={form.employment?.cert_type} onChange={(v) => setNested("employment", "cert_type", v)} options={["", "Part 91", "Part 91K", "Part 135", "Part 121", "Part 125"]} />
              <FormField label="Years with employer" value={form.employment?.years_with_employer} onChange={(v) => setNested("employment", "years_with_employer", v)} inputMode="numeric" pattern={NUM} />
            </div>
            <FormField label="Previous relevant employers (last 5 years) — one per line" value={form.employment?.previous_employers} onChange={(v) => setNested("employment", "previous_employers", v)} textarea rows={4} className="mt-4" />
          </Section>

          {/* Attestation */}
          <Section title="Applicant Attestation">
            <div className="text-white/70 text-sm mb-4">
              I certify that the above information is true and complete to the best of my knowledge and that I authorize
              PrivateJetPilots.com and its insurance partners (including Wholesure / Acrisure) to verify these facts and
              share them with underwriters for the purpose of quoting insurance or adding me as additionally insured.
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Applicant signature (typed name)" value={form.attestation?.signature} onChange={(v) => setNested("attestation", "signature", v)} testId="phf-signature" />
              <FormField label="Date" value={form.attestation?.signed_at} onChange={(v) => setNested("attestation", "signed_at", v)} type="date" testId="phf-signed-at" />
            </div>
          </Section>

          {/* Live PJP context */}
          <Card>
            <div className="flex items-center gap-3 mb-4"><Clock className="h-5 w-5 text-sky" /><div className="font-display text-xl text-white">Live PJP Flight Log</div></div>
            <div className="grid md:grid-cols-3 gap-6">
              <Row label="Sessions logged"       value={(phf.logged_by_pjp.sessions || 0).toString()} />
              <Row label="Hours from PJP Live"   value={(phf.logged_by_pjp.hours_from_pjp_live || 0).toString()} />
              <Row label="Anticipated at renewal"value={form.time?.anticipated_next_year || "—"} />
            </div>
          </Card>

          <div className="flex flex-wrap gap-3 items-center pt-2">
            <button onClick={save} disabled={saving} className="btn" data-testid="phf-save-footer" style={{ background: "#4B9CFF", color: "#0A1220" }}>
              {saving ? "Saving…" : <><Save className="h-4 w-4" /> Save PHF</>}
            </button>
            <button disabled className="btn btn-ghost cursor-not-allowed opacity-60" title="Acrisure integration coming next">
              <Send className="h-4 w-4" /> Send to Acrisure (soon)
            </button>
            <div className="text-[11px] text-white/45 mono ml-auto">Generated {new Date(phf.generated_at).toLocaleString()}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Section helpers ---------------- */

function Section({ title, icon: Icon, children }) {
  return (
    <Card>
      <div className="flex items-center gap-3 mb-4">
        {Icon && <Icon className="h-5 w-5 text-sky" />}
        <div className="font-display text-xl text-white">{title}</div>
      </div>
      {children}
    </Card>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="eyebrow-muted mb-1">{label}</div>
      <div className={mono ? "mono text-sm text-sky" : "text-white"}>{value || "—"}</div>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", options, textarea, rows = 2, className = "", testId, inputMode, pattern }) {
  return (
    <div className={className}>
      <div className="eyebrow-muted mb-2">{label}</div>
      {options ? (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky/60">
          {options.map((o) => <option key={o} value={o}>{o || "— select —"}</option>)}
        </select>
      ) : textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testId} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky/60" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} type={type} inputMode={inputMode} pattern={pattern} data-testid={testId} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky/60" />
      )}
    </div>
  );
}

function RepeatableTypeTable({ rows, onChange }) {
  const set = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const add = () => onChange([...rows, { type: "", total: "", last_12_months: "", pic_or_sic: "" }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="border-b border-white/[0.08]">
              <th className="text-left mono text-[10px] text-white/45 tracking-widest px-2 py-2">Type / Make-Model</th>
              <th className="text-left mono text-[10px] text-white/45 tracking-widest px-2 py-2">Total Hours</th>
              <th className="text-left mono text-[10px] text-white/45 tracking-widest px-2 py-2">Last 12 Months</th>
              <th className="text-left mono text-[10px] text-white/45 tracking-widest px-2 py-2">PIC/SIC</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-4 text-white/40 text-sm">No aircraft types yet. Click "Add row" below.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-white/[0.04]">
                <td className="px-2 py-1.5">
                  <select value={r.type || ""} onChange={(e) => set(i, "type", e.target.value)} data-testid={`phf-tit-type-${i}`} className="w-full bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-sm text-white">
                    <option value="">— select type —</option>
                    {AIRCRAFT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {r.type === "Other (type in below)" && (
                    <input value={r.type_other || ""} onChange={(e) => set(i, "type_other", e.target.value)} placeholder="Custom type" className="w-full mt-1 bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-sm text-white" />
                  )}
                </td>
                <td className="px-2 py-1.5"><input value={r.total || ""} onChange={(e) => set(i, "total", e.target.value)} inputMode="numeric" pattern="^[0-9]*$" className="w-24 bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-sm text-white" /></td>
                <td className="px-2 py-1.5"><input value={r.last_12_months || ""} onChange={(e) => set(i, "last_12_months", e.target.value)} inputMode="numeric" pattern="^[0-9]*$" className="w-24 bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-sm text-white" /></td>
                <td className="px-2 py-1.5"><select value={r.pic_or_sic || ""} onChange={(e) => set(i, "pic_or_sic", e.target.value)} className="bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-sm text-white"><option value="">—</option><option value="PIC">PIC</option><option value="SIC">SIC</option><option value="Both">Both</option></select></td>
                <td className="px-2 py-1.5"><button onClick={() => remove(i)} className="text-aog hover:text-white text-xs mono">REMOVE</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={add} data-testid="phf-add-type-row" className="mt-3 mono text-[11px] tracking-widest text-sky hover:text-white">+ ADD ROW</button>
    </div>
  );
}

function defaultForm() {
  return {
    subject: {},
    medical: {},
    time: {},
    time_in_type: [],
    training: {},
    claims: {},
    employment: {},
    attestation: {},
  };
}
